<template>
  <div>
    <div
      v-if="!hideCalendar"
      class="inline-calendar-area"
    >
      <!-- Activity calendar -->
      <div ref="calendar-area" :style="`min-height: ${calendarAreaMinHeight}`">
        <div v-show="!loadingCalendar">
          <b-datepicker
            inline
            :first-day-of-week="1"
            v-model="date"
            v-on:change-month="calendarMonthChanges"
            v-on:change-year="calendarYearChanges"
            :events="activitiesDates"
            :min-date="minDate()"
            :max-date="maxDate()"
            indicators="bars"
            :nearby-month-days="false"
          />
        </div>

        <!-- Loading activity calendar -->
        <div v-if="loadingCalendar">
          <spinner />
        </div>
      </div>
    </div>

    <div class="planned-activities-list">
      <div v-if="!loadingDay">
        <div v-if="groupPlannedActivities().length > 0">
          <!-- Group activity card -->
          <div
            class="card planned-activity-card mt-3"
            v-for="(groupActivity, index) in groupPlannedActivities()"
            :key="`group-activity-${index}`"
          >
            <div class="card-content">
              <!-- Planned activity title -->
              <p class="planned-activity-title">
                {{ groupActivity.activity.name }}
              </p>

              <!-- Planned activity description -->
              <p
                class="planned-activity-description text-grey"
                v-if="groupActivity.activity.description"
                v-html="groupActivity.activity.description.replace(/\n/g, '<br>')"
              />

              <!-- Planned activity duration -->
              <p
                v-if="showDuration(groupActivity.activity)"
                class="planned-activity-duration"
              >
                <b-icon
                  size="is-small"
                  icon="clock-outline"
                  class="mr-0 ml-0"
                />
                {{ $t("duration") }}
                {{ groupActivity.activity.duration }}
              </p>

              <planned-activities-row
                :group-activity="groupActivity"
                :slotable="slotable"
                :max-slotable-places="maxSlotablePlaces"
                :slotable-callback="slotableCallback"
                :show-remaining-place="showRemainingPlace(groupActivity.activity)"
                :time-slot-format="timeFormat(groupActivity.activity)"
                :get-daily-activity="dailyActivity"
                :direct-to-card="directToCard"
              />
            </div>
          </div>
        </div>
        <div v-else-if="isFullDate(date)">
          <p class="text-danger text-center mt-4">
            {{ $t("allIsFull", { date: humanizeDate(date) }) }}
          </p>
        </div>
        <div v-else>
          <p class="text-grey text-center">
            {{ $t("noCoursesAvailableOn", { date: humanizeDate(date) }) }}
          </p>
        </div>

        <!-- Next date btn if have next date -->
        <div v-if="nextDate && !hideCalendar" class="text-center">
          <b-button
            class="mt-4"
            size="is-small"
            type="is-light"
            rounded
            icon-left="calendar-arrow-right"
            @click="date = nextDate"
          >
            {{ $t("nextDateAt", { date: humanizeDate(nextDate) }) }}
          </b-button>
        </div>

        <!-- Next month if have no next date -->
        <div v-if="!nextDate && !hideCalendar" class="text-center">
          <b-button
            class="mt-4"
            size="is-small"
            type="is-light"
            rounded
            icon-left="calendar-arrow-right"
            @click="date = nextMonth"
          >
            {{ $t("nextMonth", { month: humanizeDate(nextMonth, "MMMM") }) }}
          </b-button>
        </div>
      </div>
      <div v-if="loadingDay">
        <spinner :show-text="false" />
      </div>
    </div>
  </div>
</template>

<script>
import { LocaleMixin } from "@/mixins/LocaleMixin";
import { HelpersMixin } from "@/mixins/HelpersMixin";
import { DateHelpers } from "@/mixins/DateHelpers";
import { CustomisationMixin } from "@/mixins/CustomisationMixin";
import cug from "../services/cug";
import Spinner from "./shared/Spinner";
import VueI18n from "vue-i18n";
import moment from "../services/momentjs";
import PlannedActivitiesRow from "@/components/partial/PlannedActivitiesRow";

export default {
  name: "ActivityCalendar",
  components: { PlannedActivitiesRow, Spinner },
  mixins: [LocaleMixin, HelpersMixin, DateHelpers, CustomisationMixin],
  props: {
    activityIds: {
      type: String,
      require: false,
      default: "*"
    },
    activityProductIds: {
      type: [String, Array],
      require: false,
      default: null
    },
    slotable: {
      type: Boolean,
      default: false
    },
    maxSlotablePlaces: {
      type: [Number, String],
      default: null
    },
    slotableCallback: {
      type: Function,
      default: null
    },
    openAt: {
      type: String,
      default: null
    },
    hideCalendar: {
      type: Boolean,
      default: false
    },
    directToCard: {
      type: Boolean,
      required: false
    },

    // TODO : Delete after widget-options depreciation
    widgetOptions: {
      type: String,
      required: false,
      default: "showRemainingPlace=true;timeSlotFormat=HH[h]mm;showDuration=true"
    }
  },

  data() {
    return {
      date: null,
      calendarMonth: null,
      calendarYear: null,
      calendarGettingMonths: [],
      calendarAreaMinHeight: "0px",

      activitiesDates: [],

      activities: [],
      plannedActivities: [],

      loadingCalendar: true,
      loadingDay: true,

      selectedHour: null
    };
  },

  computed: {
    calendarFocusDate() {
      const month = ("0" + this.calendarMonth).slice(-2);
      return `${this.calendarYear}-${month}-01`;
    },

    nextDate() {
      for (const activityDate of this.activitiesDates) {
        if (moment(activityDate.date).isAfter(moment(this.date).hour(23).minute(59)) && activityDate.type === "activity-color") {
          return activityDate.date;
        }
      }
      return null;
    },

    nextMonth() {
      return moment(this.calendarFocusDate)
        .add(1, "month")
        .toDate();
    }
  },

  watch: {
    date() {
      this.loadingDay = true;
      setTimeout(() => {
        this.dailyActivity();
      }, 300);
    },

    calendarFocusDate() {
      this.activitiesInMonth(this.calendarFocusDate);
    }
  },

  created() {
    let urlParams = new URLSearchParams(window.location.search);
    const paramDate = this.openAt || urlParams.get("date");

    this.date = paramDate ? new Date(paramDate) : new Date();
    this.calendarMonth = this.date.getMonth() + 1;
    this.calendarYear = this.date.getFullYear();
  },

  beforeMount() {
    this.activitiesInMonth(this.shortDate(new Date().setDate(1))).then(() => {
      this.dailyActivity();
    });
  },

  methods: {
    calendarMonthChanges(month) {
      this.calendarMonth = month + 1;
    },

    calendarYearChanges(year) {
      this.calendarYear = year;
    },

    activitiesIdsArray() {
      if (this.activityIds === "*") {
        return [];
      } else {
        return this.activityIds.replace(/\s/g, "").split(",");
      }
    },

    activityProductIdsArray() {
      if (this.activityProductIds === "*" || this.activityProductIds === null) {
        return [];
      } else if (this.activityProductIds.constructor === Array) {
        return this.activityProductIds;
      } else {
        return this.activityProductIds.replace(/\s/g, "").split(",");
      }
    },

    minDate() {
      return moment()
        .subtract(1, "day")
        .toDate();
    },

    maxDate() {
      return moment()
        .add(1, "year")
        .endOf("month")
        .toDate();
    },

    // Get activities in current calendar month
    activitiesInMonth(date) {
      if (this.calendarGettingMonths.includes(date)) {
        return false;
      } else {
        this.calendarGettingMonths.push(date);
      }

      this.loadingCalendar = true;
      return new Promise((resolve, reject) => {
        cug
          .monthlyActivities({
            month: date,
            activity_ids: this.activitiesIdsArray(),
            activity_product_filter: this.activityProductIdsArray()
          })
          .then(resp => {
            this.activities = resp.data;
            for (let activity of this.activities) {
              if (moment(activity.date).isBefore(moment().subtract(1, "day"))) continue;

              this.activitiesDates.push({
                date: new Date(activity.date),
                type: "activity-color"
              });
            }

            // Sort activities by date
            this.activitiesDates.sort(function(a, b) {
              const x = a.date;
              const y = b.date;
              return x < y ? -1 : x > y ? 1 : 0;
            });

            // get activities without remaining places
            this.activitiesFull(date);

            resolve(resp);
          })
          .catch(err => {
            reject(err);
          })
          .finally(() => {
            this.loadingCalendar = false;
            setTimeout(() => {
              this.calendarAreaMinHeight = `${this.$refs["calendar-area"].offsetHeight}px`;
            }, 500);
          });
      });
    },

    activitiesFull(date) {
      cug
        .monthlyFullActivities({
          month: date,
          activity_ids: this.activitiesIdsArray()
        })
        .then(resp => {
          for (let dateFull of resp.data) {
            if (moment(dateFull).isBefore(moment().subtract(1, "day"))) {
              continue;
            }
            this.activitiesDates.push({
              date: new Date(dateFull),
              type: "activity-full-color"
            });
          }
        });
    },

    isFullDate(date) {
      for (const fullDate of this.activitiesDates) {
        if (
          fullDate.date.toDateString() === date.toDateString() &&
          fullDate.type === "activity-full-color"
        ) {
          return true;
        }
      }
      return false;
    },

    // Get activities in selected day
    dailyActivity() {
      cug
        .dailyActivities({
          day: this.shortDate(this.date),
          activity_ids: this.activitiesIdsArray(),
          activity_product_filter: this.activityProductIdsArray()
        })
        .then(rep => {
          this.plannedActivities = rep.data;
          this.groupPlannedActivities();
        })
        .finally(() => {
          this.loadingDay = false;
        });
    },

    groupPlannedActivities() {
      const group = {};
      for (const plannedActivity of this.plannedActivities) {
        const key = `${plannedActivity.activity_id}${plannedActivity.duration}`;
        group[key] = (group[key] || {
          activity: {
            name: plannedActivity.name,
            description: plannedActivity.description,
            id: plannedActivity.id,
            duration: this.minutesDurationToHours(plannedActivity.duration),
            online_sales_parameters: plannedActivity.online_sales_parameters
          },
          plannedActivities: []
        });
        group[key]["plannedActivities"].push(plannedActivity);
      }
      return Object.values(group);
    },

    showDuration(activity) {
      if (activity.online_sales_parameters?.hide_duration) {
        return !activity.online_sales_parameters.hide_duration;
      } else if (this.customisation.showDuration) {
        return this.customisation.showDuration;
      } else {
        return true;
      }
    },

    showRemainingPlace(activity) {
      if (activity.online_sales_parameters?.hide_remaining_place) {
        return !activity.online_sales_parameters.hide_remaining_place;
      } else if (this.customisation.showRemainingPlace) {
        return this.customisation.showRemainingPlace;
      } else {
        return true;
      }
    },

    timeFormat(activity) {
      if (activity.online_sales_parameters?.time_format) {
        return activity.online_sales_parameters.time_format;
      } else if (this.customisation.timeSlotFormat) {
        return this.customisation.timeSlotFormat;
      } else {
        return "HH[h]mm";
      }
    }
  },

  // Translate component
  mounted() {
    this.$i18n.locale = this.getLocale();
  },
  i18n: new VueI18n({
    locale: "fr",
    messages: {
      en: {
        noCoursesAvailableOn: "No courses available on {date}",
        nextDateAt: "Next date at {date}",
        nextMonth: "See dates in {month}",
        chooseYourSlot: "Choose your slot :",
        duration: "Duration",
        allIsFull: "All our courses are full on {date}"
      },
      fr: {
        noCoursesAvailableOn: "Pas de cours disponible le {date}",
        nextDateAt: "Date suivante le {date}",
        nextMonth: "Voir les dates en {month}",
        chooseYourSlot: "choisissez votre créneaux :",
        duration: "Durée",
        allIsFull: "Tous nos cours sont complets le {date}"
      }
    }
  })
};
</script>

<style lang="scss" src="../assets/styles/buefy/base.scss"></style>
<style lang="scss">
.activity-color {
  background-color: #43a047;
}
.activity-full-color {
  background-color: #e23f3f;
}

.inline-calendar-area {
  .dropdown-menu {
    position: relative;
  }
}

.planned-activities-list {
  margin-top: 1em;
  .planned-activity-card {
    .planned-activity-title {
      text-transform: capitalize;
      margin-bottom: 0;
      font-size: 1.4em;
    }
    .planned-activity-description {
      margin-bottom: 0.3em;
    }
    .planned-activity-duration {
      margin-bottom: 0.5em;
    }
    .card-content {
      padding: 0.7rem;
      .max-width-column {
        max-width: 110px;
      }
    }
  }
}

</style>
